import React, {
  useCallback,
  useState,
  useRef,
  createContext,
  useContext,
} from 'react';
import { useSelector } from 'react-redux';
import usePushProvision, {
  PushProvisionError,
} from 'hooks/requests/pushProvision';
import log from 'loglevel';
import { Wallet, removeWallet } from 'state/instantCredit';
import { selectTenant } from 'state/tenant';
import { map, includes } from 'ramda';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from './requests/fetchUtils';
import { useSnackbar } from 'notistack';
import { IdiSdk, IdiSdkInit } from 'window';
import { useDispatch } from 'react-redux';
import useTranslate from './useTranslate';
import loadScript from 'utils/loadScript';

interface InitOptions {
  firstName: string;
  lastName: string;
  appleButtonId?: string;
}

type IdiResponseCallback = NonNullable<Parameters<IdiSdk['initialize']>[2]>;

type IdiSdkFunctions = {
  initialize: (options: InitOptions) => void;
  pushProvision: (wallet: Wallet) => void;
  inProgress: boolean;
  isReady: boolean;
  loadIdiSdkScript: () => Promise<void>;
};

type IdiSdkHook = () => IdiSdkFunctions;

const IdiSdkContext = createContext<IdiSdkFunctions | null>(null);

// Uncomment the following and call it in useIdiSdkScript after loading
// to mock the IdiSdk scripts operation. Maybe useful for testing
// locally, but be sure not to commit/deploy it.
/*
const mockIdiSdkScript = () => {
  const state: {
    selectionCallback: Parameters<IdiSdk['initialize']>[1] | undefined;
    responseCallback: IdiResponseCallback | undefined;
  } = {
    selectionCallback: undefined,
    responseCallback: undefined,
  };
  window.idiSDK = {
    initialize: function(options, selectionCallback, responseCallback) {
      console.log('idi', 'init with options', options);
      state.selectionCallback = selectionCallback;
      state.responseCallback = responseCallback;
    },
    selection: consumerFacingEntityName => {
      console.log('idi', 'select', consumerFacingEntityName);
      setTimeout(() => {
        if (state.selectionCallback) {
          state.selectionCallback({ payload: 'passthru' });
        }
      }, 10);
    },
    addCard: passthruToIdiSdk => {
      console.log('idi', 'addCard', passthruToIdiSdk);
      if (state.responseCallback) {
        setTimeout(() => {
          if (state.responseCallback) {
            state.responseCallback({
              result: 'success',
              message: 'success',
            });
          }
        }, 200);
      }
    },
    interpret: window.idiSDK.interpret,
  };
};
*/

const SCRIPT_PATHS = [
  `${process.env.PUBLIC_URL}/wallet/idi-sdk.2.0.js`,
  `${process.env.PUBLIC_URL}/wallet/google.integration.min.js`,
  `${process.env.PUBLIC_URL}/wallet/initAddToAppleWallet.js`,
  `${process.env.PUBLIC_URL}/wallet/s2p.min.js`,
];

const useIdiSdkScript = () => {
  const [isReady, setIsReady] = useState(false);
  // const loadScript = useScript();
  const loadIdiSdkScript = useCallback(async () => {
    await Promise.all(
      map(url => {
        return loadScript(url);
      }, SCRIPT_PATHS),
    );
    setIsReady(true);
    return;
  }, []);
  return { isReady, loadIdiSdkScript };
};

const IdiSdkProvider: React.FC = ({ children }) => {
  const [sendPushProvision] = usePushProvision();
  const [inProgress, setInProgress] = useState(false);
  const { isReady, loadIdiSdkScript } = useIdiSdkScript();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const t = useTranslate();

  // Store the selected wallet ID that the user selected.
  // This value is need to call the pushProvision endpoint.
  //
  // We need to use a ref for it because we want the selectionCallback not to
  // be redefined when lastSelectedWalletId change. If selectionCallback is
  // redefined, unless initialize is called again, the SDK will still have a
  // reference to the old function with the outdated lastSelectedWalletId.
  //
  // Ideally, since walletId is required for invoking pushProvisioning, the
  // selection function should have accepted the walletId, and carry it all the
  // way through to return in the selectionCallback. That way, we can guarantee
  // a single walletId in a single selection cycle.
  //
  // Because it doesn't, we need to store this ourselves. We also block the
  // pushProvision callback if the previous invocation is still in progress to
  // guarantee that only one walletId is ever selected. That way, when the
  // selectionCallback is called, the lastSelectedWalletId is always correct.
  //
  // As a side note, the selection method in the SDK is actually currently
  // synchronous. So it could have returned passthruFromIdiSdk immediately
  // instead separate in a a selectionCallback defined at initialization time.
  // That would have greatly simplify the selection cycle. But because of this
  // architecture, we can't guarantee that the SDK will never becomse async in
  // the future.
  const lastSelectedWallet = useRef<Wallet | null>(null);

  const showResult = useCallback(
    (result: 'success' | 'failure' | 'cancel' | 'error' | undefined) => {
      let message;
      let snackbarType: 'success' | 'error' = 'success';

      if (includes(result, ['success', 'failure', 'cancel'])) {
        message = t(`pushProvisioning.message.${result}`);
      } else {
        message = t(`pushProvisioning.message.error`);
      }
      if (result !== 'success') {
        snackbarType = 'error';
      }
      enqueueSnackbar(message, { variant: snackbarType });
    },
    // The dependencies must be stable. Because the callbacks using this
    // function are only set up once when SDK initialize, if this function gets
    // replaced, those callbacks will not have reference to the new one.
    [enqueueSnackbar, t],
  );

  const selectionCallback = useCallback(
    async (response: { payload: string }) => {
      if (!lastSelectedWallet.current) {
        throw new Error(
          'Should not reach this point without having selected a wallet',
        );
      }
      try {
        const result = await sendPushProvision({
          walletId: lastSelectedWallet.current.walletId,
          passthruFromIdiSdk: response.payload,
          consumerFacingEntityName:
            lastSelectedWallet.current.consumerFacingEntityName,
        });

        window.idiSDK.addCard(result.passthruToIdiSdk);
        // Push provisioning is still in process until we get a result back,
        // so we don't have to setInProgress(false) here. It's done in
        // responseCallback.
      } catch (e) {
        // Because selection and selectionCallback are done separately, it's
        // hard to chain them together into a promise chain. This means that
        // the caller of selection can't catch the PushProvisionError, so we
        // will just enqueue the snackbar here.
        //
        // Chaining maybe possible by creating a promise on selection and store
        // it in a ref, then resolve/reject it in selectionCallback. That'll
        // require more statefulness logic that we don't have time for right
        // now.
        if (catchOnly(e, NonClientError, PushProvisionError)) {
          showResult('error');
          setInProgress(false);
        }
      }
    },
    [sendPushProvision, showResult],
  );

  const responseCallback: IdiResponseCallback = useCallback(
    response => {
      const selectedWallet = lastSelectedWallet.current;

      if (!selectedWallet) {
        throw new Error(
          'Response callback being called without having a selected wallet',
        );
      }

      if (response.result === 'success') {
        dispatch(removeWallet(selectedWallet));
        lastSelectedWallet.current = null;
      }

      showResult(response.result);

      setInProgress(false);
    },
    [dispatch, showResult],
  );

  const tenant = useSelector(selectTenant);

  const initialize = useCallback(
    async (options: InitOptions) => {
      if (
        !tenant.enabledFeatures.wallet ||
        tenant.enabledFeatures.wallet.type !== 'permanentCard'
      ) {
        throw new Error(
          'Trying to initialize the IDI SDK without a permanentCard wallet.',
        );
      }

      if (isReady) {
        throw new Error(
          'Initializing the IDI SDK multiple times is not allowed',
        );
      }
      try {
        await loadIdiSdkScript();
      } catch (e) {
        console.error('Failed to load IDI scripts');
        return;
      }

      log.debug('Initializing IDI SDK');
      const { firstName, lastName, appleButtonId } = options;
      const attributes: IdiSdkInit = {
        cardholderFirstName: firstName,
        cardholderLastName: lastName,
        cardNickname: firstName,
        localeLanguage: 'en',
        localeCountry: 'US',
        isTestEnvironment: !tenant.overrideConfig.production,

        // eslint-disable-next-line @typescript-eslint/camelcase
        GOOGLE_hl: 'en_US',

        // eslint-disable-next-line @typescript-eslint/camelcase
        GOOGLE_integratorId:
          tenant.enabledFeatures.wallet.permanentCardOptions.googleIntegratorId,

        // eslint-disable-next-line @typescript-eslint/camelcase
        APPLE_buttonId: appleButtonId,
      };

      window.idiSDK.initialize(attributes, selectionCallback, responseCallback);
    },
    [isReady, loadIdiSdkScript, selectionCallback, responseCallback, tenant],
  );

  const pushProvision = useCallback(
    (wallet: Wallet) => {
      if (inProgress) {
        throw new Error(
          'Trying to pushProvision while a previous invocation is still in progress',
        );
      }
      setInProgress(true);
      lastSelectedWallet.current = wallet;
      console.log('setting lastSelectedWallet', wallet);

      window.idiSDK.selection(wallet.consumerFacingEntityName);
    },
    [inProgress],
  );

  const context = {
    initialize,
    pushProvision,
    inProgress,
    isReady,
    loadIdiSdkScript,
  };
  return (
    <IdiSdkContext.Provider value={context}>{children}</IdiSdkContext.Provider>
  );
};

const useIdiSdk: IdiSdkHook = () => {
  const context = useContext(IdiSdkContext);
  if (context === null) {
    throw new Error('trying to use IdiSdk without loading it');
  }
  return context;
};

export default useIdiSdk;
export { IdiSdkProvider };
